<template>
  <div class="lf-selector-tree">
    <div v-show="isLoading" class="loading">
      <SpinnerLayerComponent class="spinner-layer" :loadingText="spinnerText" />
    </div>
    <v-radio-group v-if="hasTemplates" v-model="templateSelected" :key="componentKey" @change="getTemplate">
      <v-treeview
        v-show="!isLoading"
        ref="treeView"
        dense
        selected-color="#001978"
        :items="templateTree"
        item-text="name"
        item-disabled="locked"
        :open="itemsOpenend"
      >
        <template v-if="!isLoading" v-slot:label="{ item }">
          <span v-if="item.isDirectory" class="is-directory" @click="onClickDirectory(item)">
            {{ item.name }}
            <span v-if="directoryIsEmpty(item)">
              {{ ' (' + texts.emptyFolder + ')' }}
            </span>
          </span>
          <span v-else>{{ item.name }}</span>
        </template>
        <template v-if="!isLoading" v-slot:prepend="{ item }">
          <SpinnerLayerComponent v-if="item.loading" class="spinner-layer-item" />
          <span
            v-if="item.isDirectory && !item.loading"
            :class="directoryIcon(item.id)"
            @click="onClickDirectory(item)"
          ></span>
          <div v-else class="e-filemanager">
            <v-radio v-if="!item.loading" class="radio-tree" :value="item" :on-icon="icons.on" :off-icon="icons.off" />
          </div>
        </template>
      </v-treeview>
    </v-radio-group>
    <div v-else class="no-result-dialog">
      <NoResultsComponent
        v-if="!isLoading"
        :noResultsIcon="icons.document"
        :noResultsFirstText="texts.templatesNotFound"
        :noResultsSecondText="texts.templatesFromSettings"
        :withBorders="false"
        :verticalAlignCenter="true"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
// IMPORTS ==========================================================
import { ref, computed, onMounted, onBeforeUnmount, onBeforeMount } from 'vue'
import { Icons } from '@/icons/icons'
import SpinnerLayerComponent from '@/components/Spinner/SpinnerLayerComponent.vue'
import NoResultsComponent from '@/components/search/NoResults/NoResultsComponent.vue'
import useTemplates from '@/components/template/composables/useTemplates'
import type { ITemplate } from '@/store/modules/template/templateTypes'
import { useI18n } from 'vue-i18n-composable'
import { configuracionPanelURLS } from '@/router/routes/urlRoutes'
import type { TemplateTreeItem } from '@/components/template/TemplateSelectorComponent/types/TemplateTypes'

const { t } = useI18n()

// DEFINE PROPS  ==========================================================
const props = defineProps({
  prop: {
    type: String,
    default: ''
  },
  idEntityType: {
    type: [Number, String],
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  generateTemplate: {
    type: Boolean,
    default: false
  }
})

// EVENTS  ==========================================================
const emit = defineEmits(['templateSelected'])

// COMPOSABLES ==========================================================
const { templateTree, templateTreeEntityType, fetchTemplateTree, setTreeEntityType } = useTemplates()

// DATA ==========================================================
const componentKey = ref(0)
const isLoading = ref(props.loading)
const templateSelected = ref<ITemplate | null>(null)
const itemsOpenend = ref<number[]>([])
const generatingTemplate = ref(props.generateTemplate)

// CONSTANTS ==========================================================
const texts = {
  noTemplatesMessage: t('components.template_selector_tree.no_templates_message'),
  createTemplate: t('components.template_selector_tree.create_template'),
  noThanks: t('components.template_selector_tree.no_thanks'),
  templatesNotFound: t('components.file_manager.dialogs.templates_not_found'),
  generatingTemplate: t('components.file_manager.dialogs.generating_document'),
  emptyFolder: t('components.file_manager.advises.empty_folder'),
  templatesFromSettings: t('components.file_manager.dialogs.templates_from_settings', {
    link: configuracionPanelURLS.DOCUMENTS_TEMPLATES
  })
}

const icons = {
  document: Icons.ARCHIVE_EMPTY,
  folder: Icons.FOLDER,
  folderOpen: Icons.FOLDER_OPEN,
  on: Icons.RADIO_ACTIVE,
  off: Icons.CIRCLE_O,
  close: Icons.CLOSE
}

// COMPUTED ==========================================================
const spinnerText = computed(() => {
  return generatingTemplate.value ? texts.generatingTemplate : ''
})

const hasTemplates = computed(() => {
  return Array.isArray(templateTree.value) && templateTree.value.length > 0
})

// METHODS ==========================================================
const updateComponent = () => {
  componentKey.value += 1
  return componentKey.value
}

const getTemplateTreeData = async () => {
  const json = {
    idEntityType: props.idEntityType
  }
  await fetchTemplateTree(json)
}

const directoryIsOpen = (itemId: number) => {
  return itemsOpenend.value.includes(itemId)
}

const directoryIcon = (itemId: number) => {
  return directoryIsOpen(itemId) ? icons.folderOpen : icons.folder
}

const directoryIsEmpty = (item: TemplateTreeItem) => {
  return directoryIsOpen(item.id) && typeof item.children !== 'undefined' && item.children.length <= 0
}

const addInitiallyOpenItems = (itemId: number) => {
  itemsOpenend.value.push(itemId)
}

const removeInitiallyOpenItems = (itemId: number) => {
  itemsOpenend.value = itemsOpenend.value.filter((_itemId) => _itemId !== itemId)
}

const updateOpenItems = (itemId: number) => {
  if (!directoryIsOpen(itemId)) {
    addInitiallyOpenItems(itemId)
    return true
  } else {
    removeInitiallyOpenItems(itemId)
    return false
  }
}

const spinnerTreeItem = (item: TemplateTreeItem, loading: boolean) => {
  item.loading = loading
  updateComponent()
}

const addChildrenTreeItems = async (item: TemplateTreeItem) => {
  const childrenUndefined = typeof item.children === 'undefined'
  let data = !childrenUndefined ? item.children : []

  if (childrenUndefined) {
    data = await fetchTemplateTree({
      idEntityType: props.idEntityType,
      id: item.id,
      getData: true
    })
  }

  item.children = data || []
  item.locked = (data?.length || 0) <= 0
}

const onClickDirectory = async (item: any) => {
  const updateOpenItemsResult = updateOpenItems(item.id)
  if (!updateOpenItemsResult) {
    return false
  }

  spinnerTreeItem(item, true)

  await addChildrenTreeItems(item).then(() => {
    spinnerTreeItem(item, false)
  })
}

const getTemplate = () => {
  emit('templateSelected', templateSelected.value)
}

// HOOKS ==========================================================
onBeforeMount(() => {
  isLoading.value = true
})

onMounted(async () => {
  if (props.idEntityType !== templateTreeEntityType.value || !hasTemplates.value) {
    setTreeEntityType(Number(props.idEntityType))
    await getTemplateTreeData()
  }
  isLoading.value = props.loading
  generatingTemplate.value = props.generateTemplate
})

onBeforeUnmount(() => {
  templateSelected.value = null
  isLoading.value = true
  generatingTemplate.value = true
})
</script>

<style lang="scss" scoped>
@import '~@/styles/partials/components/template/selector';
</style>
